/**
 * Generic iOS-style bottom drawer controller.
 *
 * Markup convention:
 *   <dialog data-js="bottom-drawer" id="my-drawer">
 *     <div class="bottom-drawer__sheet" data-js="bottom-drawer-sheet">
 *       <div class="bottom-drawer__handle" data-js="bottom-drawer-handle" aria-hidden="true">
 *         <span class="bottom-drawer__handle-bar"></span>
 *       </div>
 *       <div class="bottom-drawer__header">…</div>
 *       <div class="bottom-drawer__body">…</div>
 *       <div class="bottom-drawer__footer">…</div>
 *     </div>
 *   </dialog>
 *
 * Triggers (declarative):
 *   [data-drawer-open="drawer-id"]  — opens the drawer with matching id
 *   [data-drawer-close]             — closes the closest parent drawer
 *
 * API (imperative):
 *   window.BottomDrawer.open(el | id)
 *   window.BottomDrawer.close(el | id)
 */
(function () {
  if (window.BottomDrawer) return;

  /**
   * Resolve a drawer element from an ID string or element ref.
   * @param {string|HTMLDialogElement} ref
   * @returns {HTMLDialogElement|null}
   */
  function resolve(ref) {
    if (typeof ref === "string") return document.getElementById(ref);
    return ref;
  }

  /**
   * Open a bottom drawer.
   * @param {string|HTMLDialogElement} ref
   */
  function open(ref) {
    const dialog = resolve(ref);
    if (!dialog || dialog.dataset.state === "open" || dialog.dataset.opening === "true") return;

    dialog.dataset.opening = "true";

    const id = dialog.id || "bottom-drawer";
    const sheet = dialog.querySelector('[data-js="bottom-drawer-sheet"]');

    if (sheet) {
      sheet.style.transition = "";
      sheet.style.transform = "";
    }

    if (!dialog.open) {
      dialog.showModal();
      dialog.scrollTop = 0;
    }

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        dialog.dataset.state = "open";
        delete dialog.dataset.opening;
        dialog.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { id } }));
      });
    });

    window.themeUtils?.lockScroll(id);
    window.themeUtils?.emit?.("overlay-show", { source: id });

    let fired = false;
    const onEnd = function (e) {
      if (e && e.type === "transitionend") {
        if (e.target !== sheet || e.propertyName !== "transform") return;
      }
      if (fired) return;
      fired = true;
      if (sheet) sheet.removeEventListener("transitionend", onEnd);
      if (dialog.dataset.state === "open") {
        dialog.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true, detail: { id } }));
      }
    };
    if (sheet) sheet.addEventListener("transitionend", onEnd);
    setTimeout(onEnd, 400);
  }

  /**
   * Close a bottom drawer.
   * @param {string|HTMLDialogElement} ref
   */
  function close(ref, reason = "api") {
    const dialog = resolve(ref);
    if (!dialog || dialog.dataset.state !== "open") return;

    const id = dialog.id || "bottom-drawer";
    
    dialog.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { id, reason } }));

    const sheet = dialog.querySelector('[data-js="bottom-drawer-sheet"]');

    if (sheet) {
      sheet.style.transform = "";
      if (reason !== "swipe") {
        sheet.style.transition = "";
      }
    }

    dialog.dataset.state = "closing";
    window.themeUtils?.unlockScroll(id);
    window.themeUtils?.emit?.("overlay-hide", { source: id });

    let fired = false;
    const onEnd = function (e) {
      if (e && e.type === "transitionend") {
        if (e.target !== sheet || e.propertyName !== "transform") return;
      }
      if (fired) return;
      fired = true;
      if (sheet) {
        sheet.removeEventListener("transitionend", onEnd);
        sheet.style.transition = "";
      }
      if (dialog.dataset.state === "closing") {
        dialog.close();
      }
    };

    if (sheet) sheet.addEventListener("transitionend", onEnd);
    setTimeout(onEnd, 400);
  }

  /**
   * Set loading state on a bottom drawer.
   * @param {string|HTMLDialogElement} ref
   * @param {boolean} isLoading
   */
  function setLoading(ref, isLoading) {
    const dialog = resolve(ref);
    if (dialog) {
      dialog.dataset.loading = isLoading ? "true" : "false";
    }
  }

  /* ── Swipe-to-dismiss ─────────────────────────────── */

  let startY = 0;
  let currentY = 0;
  let dragging = false;
  let activeSheet = null;
  let currentTranslateY = 0;
  let touchSamples = [];

  /**
   * @param {TouchEvent} e
   */
  function onTouchStart(e) {
    const handle = e.target.closest('[data-js="bottom-drawer-handle"]');
    if (!handle) return;

    const sheet = handle.closest('[data-js="bottom-drawer-sheet"]');
    if (!sheet) return;

    const dialog = sheet.closest('[data-js="bottom-drawer"]');
    if (dialog) {
      if (dialog.dataset.state !== "open") return;
      if (dialog.dataset.responsive === "true" && window.matchMedia('(min-width: 768px)').matches) {
        return;
      }
    }

    dragging = true;
    activeSheet = sheet;
    startY = e.touches[0].clientY;
    currentY = startY;
    touchSamples = [{ y: startY, time: Date.now() }];

    const style = window.getComputedStyle(sheet);
    const transform = style.transform;
    currentTranslateY = 0;
    if (transform && transform !== "none") {
      const match = transform.match(/matrix.*\((.+)\)/);
      if (match) {
        const values = match[1].split(", ");
        currentTranslateY = parseFloat(values.length === 16 ? values[13] : values[5]) || 0;
      }
    }
    currentTranslateY = Math.max(0, currentTranslateY);

    sheet.style.transition = "none";
    sheet.style.transform = "translateY(" + currentTranslateY + "px)";
  }

  /**
   * @param {TouchEvent} e
   */
  function onTouchMove(e) {
    if (!dragging || !activeSheet) return;
    currentY = e.touches[0].clientY;
    
    touchSamples.push({ y: currentY, time: Date.now() });
    if (touchSamples.length > 5) touchSamples.shift();

    const dy = currentY - startY;
    let newY = currentTranslateY + dy;
    
    if (newY < 0) {
      newY = -Math.pow(-newY, 0.75);
    }
    
    activeSheet.style.transform = "translateY(" + newY + "px)";
  }

  function onTouchEnd() {
    if (!dragging || !activeSheet) return;
    dragging = false;

    let velocity = 0;
    if (touchSamples.length >= 2) {
      const first = touchSamples[0];
      const last = touchSamples[touchSamples.length - 1];
      const dt = last.time - first.time;
      if (dt > 0) {
        velocity = (last.y - first.y) / dt;
      }
    }

    const sheetHeight = activeSheet.offsetHeight;
    const dy = currentY - startY;
    const finalTranslateY = currentTranslateY + dy;
    const shouldDismiss = velocity > 0.5 || finalTranslateY > sheetHeight * 0.3;

    if (shouldDismiss) {
      const dialog = activeSheet.closest('[data-js="bottom-drawer"]');
      if (velocity > 0.5) {
        activeSheet.style.transition = 'transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)';
      } else {
        activeSheet.style.transition = '';
      }
      if (dialog) close(dialog, 'swipe');
    } else {
      activeSheet.style.transition = "";
      activeSheet.style.transform = "";
    }

    activeSheet = null;
    touchSamples = [];
  }

  document.addEventListener("touchstart", onTouchStart, { passive: true });
  document.addEventListener("touchmove", onTouchMove, { passive: true });
  document.addEventListener("touchend", onTouchEnd, { passive: true });

  document.addEventListener("click", function (e) {
    const openTrigger = e.target.closest("[data-drawer-open]");
    if (openTrigger) {
      e.preventDefault();
      open(openTrigger.dataset.drawerOpen);
      return;
    }

    const closeTrigger = e.target.closest("[data-drawer-close]");
    if (closeTrigger) {
      e.preventDefault();
      const dialog = closeTrigger.closest('[data-js="bottom-drawer"]');
      if (dialog) close(dialog, 'button');
      return;
    }

    // Backdrop click — dialog element itself receives the click
    if (e.target.matches('[data-js="bottom-drawer"]')) {
      close(e.target, 'backdrop');
      return;
    }

    // Global page-overlay click
    if (e.target.matches("[data-page-overlay]")) {
      const openDrawers = document.querySelectorAll(
        '[data-js="bottom-drawer"][data-state="open"]',
      );
      openDrawers.forEach(function(d) { close(d, 'backdrop'); });
    }
  });

  // Native dialog cancel (Escape key)
  // Use capture phase (true) because cancel events don't bubble
  document.addEventListener(
    "cancel",
    function (e) {
      if (e.target.matches('[data-js="bottom-drawer"]')) {
        const cancelEvent = new CustomEvent('drawer:cancel', { bubbles: true, cancelable: true });
        e.target.dispatchEvent(cancelEvent);
        if (cancelEvent.defaultPrevented) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        close(e.target, 'escape');
      }
    },
    true,
  );

  // Sync state when dialog closes natively (e.g. form method="dialog")
  document.addEventListener(
    "close",
    function (e) {
      if (e.target.matches('[data-js="bottom-drawer"]')) {
        const dialog = e.target;
        if (dialog.dataset.state !== "closed") {
          const id = dialog.id || "bottom-drawer";
          const wasClosing = dialog.dataset.state === "closing";
          dialog.dataset.state = "closed";
          if (!wasClosing) {
            window.themeUtils?.unlockScroll(id);
            window.themeUtils?.emit?.("overlay-hide", { source: id });
          }
          dialog.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true, detail: { id } }));
        }
      }
    },
    true,
  );

  window.BottomDrawer = Object.freeze({ open: open, close: close, setLoading: setLoading });
})();
